import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { GoogleIcon } from "@/components/GoogleIcon";
import { supabase } from "@/lib/supabase";

export default function SignupPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/workspace";

  useEffect(() => {
    document.title = "Create account — MACHA";
  }, []);

  const signUpEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: name },
        emailRedirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`,
      },
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    if (data.session) {
      navigate(next, { replace: true });
    } else {
      toast.success("Check your email to confirm your account.");
    }
  };

  const signInGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}` },
    });
  };

  return (
    <div className="flex min-h-[calc(100vh-3.5rem)] items-center justify-center px-5 py-16">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center">
          <Logo />
          <h1 className="mt-7 text-2xl font-semibold tracking-tight text-foreground">Create your account</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Free forever — upgrade only when it pays for itself.</p>
        </div>

        <Button
          data-testid="signup-google-btn"
          onClick={signInGoogle}
          variant="outline"
          className="mt-8 h-11 w-full justify-center gap-2.5 rounded-lg border-border text-[13.5px] font-medium"
        >
          <GoogleIcon className="h-[18px] w-[18px]" /> Sign up with Google
        </Button>

        <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
        </div>

        <form className="space-y-4" onSubmit={signUpEmail} aria-label="Sign up form">
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-[13px]">Full name</Label>
            <Input id="name" type="text" autoComplete="name" placeholder="Alex Rivera"
              value={name} onChange={(e) => setName(e.target.value)}
              data-testid="signup-name-input" className="h-10 rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-[13px]">Email</Label>
            <Input id="email" type="email" autoComplete="email" placeholder="you@studio.com"
              value={email} onChange={(e) => setEmail(e.target.value)} required
              data-testid="signup-email-input" className="h-10 rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-[13px]">Password</Label>
            <Input id="password" type="password" autoComplete="new-password" minLength={8}
              placeholder="At least 8 characters"
              value={password} onChange={(e) => setPassword(e.target.value)} required
              data-testid="signup-password-input" className="h-10 rounded-lg" />
          </div>
          <Button type="submit" disabled={loading}
            data-testid="signup-submit-btn"
            className="h-10 w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90">
            {loading ? "Creating account…" : "Create account"}
          </Button>
        </form>

        <p className="mt-8 text-center text-[13px] text-muted-foreground">
          Already have an account?{" "}
          <Link to={`/login?next=${encodeURIComponent(next)}`} className="text-foreground hover:text-primary">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
